import React from 'react';
import { TableColumn } from '../../components/Table/Table';
import { Candidate } from '../../types';
import { API_CONFIG } from '../../config/api.config';

const sourceStyles: Record<string, string> = {
  website: 'bg-blue-100 text-blue-800',
  website_form: 'bg-blue-100 text-blue-800',
  linkedin: 'bg-indigo-100 text-indigo-800',
  referral: 'bg-green-100 text-green-800',
  job_board: 'bg-purple-100 text-purple-800',
};

const sourceLabels: Record<string, string> = {
  website: 'Website', website_form: 'Website Form', linkedin: 'LinkedIn',
  referral: 'Referral', job_board: 'Job Board',
};

const statusStyles: Record<string, string> = {
  new: 'bg-gray-100 text-gray-800',
  under_review: 'bg-yellow-100 text-yellow-800',
  interview_scheduled: 'bg-blue-100 text-blue-800',
  interviewed: 'bg-indigo-100 text-indigo-800',
  hired: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const FileLink: React.FC<{ value: any; colorClass: string; fallbackLabel: string }> = ({ value, colorClass, fallbackLabel }) => {
  const fileUrl = typeof value === 'string' ? value : value?.url;
  const fullUrl = fileUrl ? (fileUrl.startsWith('http') ? fileUrl : `${API_CONFIG.BASE_URL}${fileUrl}`) : null;

  if (!fullUrl) return <span className="text-xs text-gray-400">No {fallbackLabel}</span>;

  return (
    <a href={fullUrl} target="_blank" rel="noopener noreferrer"
      className={`inline-flex items-center px-3 py-1.5 ${colorClass} rounded-md hover:opacity-80 transition-colors text-xs font-medium`}
      onClick={(e) => e.stopPropagation()}>
      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
      </svg>
      {typeof value === 'object' && value?.name ? value.name : `View ${fallbackLabel}`}
    </a>
  );
};

export const getCandidateColumns = (onViewDetails: (c: Candidate) => void): TableColumn[] => [
  { key: 'name', label: 'Name', sortable: true, render: (_: any, item: Candidate) => (
    <button onClick={() => onViewDetails(item)} className="text-left hover:text-primary-600 transition-colors duration-200">
      <div className="font-medium text-gray-900">{item.firstName} {item.lastName}</div>
      <div className="text-sm text-gray-500">{item.email}</div>
    </button>
  ) },
  { key: 'appliedPosition', label: 'Position', sortable: true },
  { key: 'experience', label: 'Experience', sortable: true, render: (value: number) => `${value} years` },
  { key: 'nationality', label: 'Nationality', sortable: true },
  { key: 'appliedDate', label: 'Applied Date', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
  { key: 'source', label: 'Source', sortable: true, render: (value: string) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${sourceStyles[value] || 'bg-gray-100 text-gray-800'}`}>
      {sourceLabels[value] || value}
    </span>
  ) },
  { key: 'status', label: 'Status', sortable: true, render: (value: string) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[value] || 'bg-gray-100 text-gray-800'}`}>
      {value.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
    </span>
  ) },
  { key: 'assignedManagerName', label: 'Manager', sortable: true },
  { key: 'resumeUrl', label: 'Resume/CV', render: (value: any) => <FileLink value={value} colorClass="bg-blue-100 text-blue-800" fallbackLabel="CV" /> },
  { key: 'applicationPdfUrl', label: 'Application PDF', render: (value: any) => <FileLink value={value} colorClass="bg-red-100 text-red-800" fallbackLabel="PDF" /> },
  { key: 'totalRating', label: 'Rating', sortable: true, render: (value: number, item: Candidate) => (
    <div className="flex items-center">
      <div className="flex items-center">
        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
          <span className="text-xs font-medium text-primary-800">{value || 0}</span>
        </div>
        <span className="ml-2 text-xs text-gray-500">/10</span>
      </div>
      {item.rating && item.rating.length > 0 && (
        <div className="ml-2 text-xs text-gray-400">({item.rating.length} point{item.rating.length !== 1 ? 's' : ''})</div>
      )}
    </div>
  ) },
];
